import whenDomReady from 'when-dom-ready';
import { clearConversation, readConversation, saveConversation, setTranscriptFieldVisible, writeConversationToForm, } from './chat-transcript-store';
import { readConversationFromUrl, stripHandoffParams } from './chat-handoff-params';
import { addBackToChatControl, hideChatEmbed, returnToChat, revealSupportSection, } from './chat-support-section';
// Restores a hand-off that has not been submitted yet.
//
// This covers two situations. On a different page it is what makes the redirect flow work at
// all - DocsBot opens its support link in a new tab, so the form is reached with a fresh page
// load. On the same page it means a reload does not throw the visitor back to a hidden form.
//
// The stored conversation doubles as the "hand-off is still open" flag: it is only ever written
// when the visitor escalates, it is removed once the form is submitted, and it expires on its own.
/**
 * A submitted form leaves `#success` in the URL - see init-bizform, which uses the same signal
 * for its own success handling.
 *
 * Read at module load, deliberately: init-bizform strips the hash from the history on DOM ready,
 * so by the time the code below runs it may already be gone.
 */
const arrivedAfterSubmit = hasSuccessHash();
function hasSuccessHash() {
    return window.location.hash
        .substring(1)
        .split('&')
        .some((pair) => decodeURIComponent(pair.split('=', 1)[0]) === 'success');
}
/**
 * Clearing is tied to a confirmed submission rather than to the submit event. The event also
 * fires when validation is about to block the submission, and clearing there would throw away a
 * transcript the visitor never managed to send.
 */
function clearOnConfirmedSubmit() {
    if (arrivedAfterSubmit) {
        clearConversation();
        return;
    }
    // Forms submitted without leaving the page announce success by changing the hash.
    window.addEventListener('hashchange', () => {
        if (hasSuccessHash()) {
            clearConversation();
        }
    });
}
function initialise() {
    clearOnConfirmedSubmit();
    // A stored conversation came through the callback and carries the verbatim transcript, so it
    // beats whatever the URL says. The URL route only applies to the popup chat, which has no
    // embedded widget to register a callback.
    let conversation = readConversation();
    if (!conversation) {
        conversation = readConversationFromUrl();
        if (conversation) {
            // Store it so it follows the same lifecycle as a callback hand-off: it survives a reload
            // and is dropped once the form is submitted.
            saveConversation(conversation);
            stripHandoffParams();
        }
    }
    if (!conversation) {
        // The form is also used on its own, without the chat - no conversation means nothing to attach.
        setTranscriptFieldVisible(false);
        return;
    }
    if (!writeConversationToForm(conversation)) {
        // No transcript field here, so this is not the page the form lives on.
        return;
    }
    // Leaves the page where it is if the visitor has already scrolled somewhere themselves.
    revealSupportSection({ respectVisitorScroll: true });
    // Reproduce the post-hand-off state, so a reload does not show the chat and the form at once.
    hideChatEmbed();
    addBackToChatControl(() => {
        clearConversation();
        returnToChat();
    });
}
whenDomReady().then(initialise);
